import type { ComponentType } from 'react';
import type { Editor } from '@tiptap/react';
import {
    ArrowsUpDownIcon,
    Bars3BottomLeftIcon,
    Bars3BottomRightIcon,
    InformationCircleIcon,
    MinusIcon
} from '@heroicons/react/24/outline';
import { DEFAULT_CALLOUT_TONE } from '../extensions/Callout';
import { DEFAULT_SPACER_SIZE } from '../extensions/Spacer';
import { applyTextAlign, TEXT_ALIGNMENTS, type TextAlignment } from '../extensions/textStyling';
import { ToolbarButton } from './Toolbar';

/** Heroicons has no centred-bars glyph, so the middle alignment draws its own. */
function AlignCenterIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            aria-hidden="true"
        >
            <path d="M4 6h16M7 12h10M5 18h14" />
        </svg>
    );
}

interface AlignMeta {
    readonly title: string;
    readonly Icon: ComponentType<{ className?: string }>;
}

const ALIGN_META: Record<TextAlignment, AlignMeta> = {
    left: { title: 'Align left', Icon: (props) => <Bars3BottomLeftIcon {...props} /> },
    center: { title: 'Align centre', Icon: AlignCenterIcon },
    right: { title: 'Align right', Icon: (props) => <Bars3BottomRightIcon {...props} /> }
};

export function CalloutToolbarGroup({ editor }: { editor: Editor }) {
    return (
        <>
            <ToolbarButton
                title="Callout / notice box"
                active={editor.isActive('callout')}
                onClick={() =>
                    editor
                        .chain()
                        .focus()
                        .toggleWrap('callout', { tone: DEFAULT_CALLOUT_TONE })
                        .run()
                }
            >
                <InformationCircleIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />
        </>
    );
}

export function LayoutToolbarGroup({ editor }: { editor: Editor }) {
    return (
        <>
            <ToolbarButton
                title="Divider"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
            >
                <MinusIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <ToolbarButton
                title="Spacer"
                active={editor.isActive('spacer')}
                onClick={() =>
                    editor
                        .chain()
                        .focus()
                        .insertContent({ type: 'spacer', attrs: { size: DEFAULT_SPACER_SIZE } })
                        .run()
                }
            >
                <ArrowsUpDownIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />
        </>
    );
}

export function TextStylingToolbarGroup({ editor }: { editor: Editor }) {
    return (
        <>
            {TEXT_ALIGNMENTS.map((alignment) => {
                const { title, Icon } = ALIGN_META[alignment];
                return (
                    <ToolbarButton
                        key={alignment}
                        title={title}
                        active={editor.isActive({ textAlign: alignment })}
                        onClick={() => applyTextAlign(editor, alignment)}
                    >
                        <Icon className="h-3.5 w-3.5" />
                    </ToolbarButton>
                );
            })}
            <ToolbarButton
                title="Highlight"
                active={editor.isActive('highlight')}
                onClick={() => editor.chain().focus().toggleMark('highlight').run()}
            >
                <span className="rounded-[3px] bg-yellow-200 px-1 font-semibold text-gray-800">
                    A
                </span>
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />
        </>
    );
}
